// src/pages/ServerDataExtractionPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ExtractionOptions from '../components/ServerDataExtraction/ExtractionOptions';
import ExtractionResults from '../components/ServerDataExtraction/ExtractionResults';
import ExtractionHistory from '../components/ServerDataExtraction/ExtractionHistory';
import ServerDataExtractionHeader from '../components/ServerDataExtraction/ServerDataExtractionHeader';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import Tips from '../components/Shared/Tips';
import PageTransition from '../components/Shared/PageTransition';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';

// --- Import BOTH Configs ---
// For initial connection check
import {
    getServerInfoAppEndpoint,
    WS_SERVER_INFO_TOPICS,
    getServerInfoUserQueueTopic
} from '../config/wsServerInfoConfig';
// For extraction actions and results
import {
    WS_SERVER_DATA_TYPES,
    WS_SERVER_DATA_TOPICS,
    getDataAppEndpoint,
    getDataUserQueueTopic
} from '../config/wsServerDataExtractionConfig';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import { useNotifications } from '../contexts/NotificationContext';
import { handleWebSocketError } from '../utils/websocketErrorHandler';
// --- --- ---

const ServerDataExtractionPage = ({openModal}) => {
    const { isAuthenticated, user, isLoading: isAuthLoading } = useAuth();
    const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
    const { serverInfo, connectionError, isConnecting, isConnectedToServer } = useServerConnection();
    const {addNotification} = useNotifications();

    // --- State Management (Mostly unchanged) ---
    // Extraction State (Managed by DataExtraction endpoint)
    const [extractionOptions, setExtractionOptions] = useState({
        serverInfo: true, motd: true, serverIcon: true, playerList: true,
        pluginList: true, deepScan: false, timeout: 5000, outputFormat: 'json'
    });
    const [extractionStatus, setExtractionStatus] = useState('idle'); // idle, extracting, error, complete, partial
    const [isExtractingLoading, setIsExtractingLoading] = useState(false); // Loading for EXTRACT request
    const [extractedData, setExtractedData] = useState(null); // Data specific to the extraction result
    const [extractionError, setExtractionError] = useState(null); // Errors from extraction process
    const [extractionProgress, setExtractionProgress] = useState(null); // Progress messages for extraction

    // History State
    const [extractionHistory, setExtractionHistory] = useState([]); // State for history
    const [isHistoryLoading, setIsHistoryLoading] = useState(false); // Loading state for history
    const [historyError, setHistoryError] = useState(null); // Error state for history

    // General State
    const [generalWsError, setGeneralWsError] = useState(null); // For WebSocket level issues
    const subscriptions = useRef({
        extractionStatus: null, extractionProgress: null, extractionError: null, history: null // For extraction
    });
    const historyRequestedRef = useRef(false); // Prevent multiple history requests
    const currentServerIpRef = useRef(null); // Tracks the IP we intend to interact with

    // --- WebSocket Message Handlers & Component Actions ---

    // --- Extraction Handlers (Using DataExtraction Topics) ---
    const handleExtractionStatusUpdate = useCallback((message, wsError) => {
        if (wsError) {
            console.error("WS Extraction Status Error:", wsError);
            setGeneralWsError("Error receiving extraction status.");
            setIsExtractingLoading(false); // Stop loading on error
            return;
        }
        console.log('%cWS EXTRACTION STATUS Received:', 'color:purple;', message);
        setGeneralWsError(null); // Clear general error

        // Check if the message corresponds to the current server
        /*if (message?.data.ip !== currentServerIpRef.current) {
             console.warn("Received Extraction Status for unexpected IP:", message?.data.ip, "Current IP:", currentServerIpRef.current);
             return; // Ignore status for wrong IP
        }*/

        switch (message?.type) {
            case WS_SERVER_DATA_TYPES.EXTRACTION_STARTED:
                setIsExtractingLoading(true);
                setExtractionStatus('extracting');
                setExtractionError(null);
                setExtractedData(null); // Clear previous data
                console.log("Extraction started:", message.message);
                setExtractionProgress({ step: "Starting", message: message.message || "Initiating extraction..." });
                break;

            case WS_SERVER_DATA_TYPES.EXTRACTION_COMPLETED:
                setIsExtractingLoading(false);
                 // Determine status based on the 'success' field WITHIN the data payload
                const success = message.data?.success === true;
                const status = success ? (message.status || 'complete') : 'error'; // Treat success=false as error
                const newData = message.data || {};
                const errorMsg = !success ? (message.data?.error || "Extraction failed") : (status === 'partial' ? (message.message || "Partial data extracted.") : null);

                setExtractionStatus(status);
                setExtractedData(newData); // Store received data regardless of success/partial
                setExtractionError(errorMsg);
                setExtractionProgress(null); // Clear progress on completion/failure

                console.log("Extraction completed/failed:", message);

                // Add to history only if successful
                if (success) {
                    const historyEntry = {
                        server: serverInfo?.hostname || currentServerIpRef.current, // Use existing hostname if available
                        ip: currentServerIpRef.current,
                        date: new Date().toISOString(),
                        status: status, // 'complete' or 'partial'
                        // Filter options based on what data was actually returned
                         extractedData: Object.keys(extractionOptions).filter(k =>
                            extractionOptions[k] === true &&
                            k !== 'timeout' && k !== 'outputFormat' && k !== 'deepScan' &&
                             ( // Check if the corresponding data field exists and has content in the result
                                 (k === 'serverInfo' && (newData.version || newData.onlinePlayers !== undefined || newData.maxPlayers !== undefined || newData.ping !== undefined)) || // Check multiple server info fields
                                 (k === 'motd' && newData.motd?.clean) || // Check specific subfield
                                 (k === 'serverIcon' && newData.icon) ||
                                 (k === 'playerList' && newData.onlinePlayers?.list?.length > 0) ||
                                 (k === 'pluginList' && newData.plugins?.length > 0) // Assuming 'plugins' field in DTO
                             )
                         )
                    };
                    setExtractionHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
                }
                break;

            // EXTRACTION_FAILED is now handled by checking success flag in COMPLETED or via ERROR topic
            // case WS_SERVER_DATA_TYPES.EXTRACTION_FAILED: ...

            // CURRENT_STATE might need adjustment if backend sends separate states
            case WS_SERVER_DATA_TYPES.CURRENT_STATE:
                 console.log("Restoring extraction state from server:", message);
                 // This should ideally only restore extraction-related state
                 // Connection state is handled by ServerInfo CURRENT_STATE if implemented
                 if (message.extraction && message.serverInfo?.ip === currentServerIpRef.current) {
                     setExtractionOptions(message.extraction.options || extractionOptions);
                     setExtractionStatus(message.extraction.status || 'idle');
                     setExtractedData(message.extraction.data || null);
                     setIsExtractingLoading(message.extraction.status === 'extracting');
                     setExtractionError(message.extraction.status === 'error' ? 'Extraction previously failed.' : null);
                     setExtractionProgress(null);
                 } else if (!message.extraction && message.serverInfo?.ip === currentServerIpRef.current) {
                    // Reset extraction state if backend state doesn't include it
                    setExtractionStatus('idle');
                    setExtractedData(null);
                    setExtractionError(null);
                    setExtractionProgress(null);
                 }
                 break;

            default:
                console.warn('Unhandled extraction status message type:', message?.type);
        }
    }, [serverInfo, extractionOptions, currentServerIpRef]); // Added currentServerIpRef

    const handleExtractionProgressUpdate = useCallback((message, wsError) => {
        if (wsError || !message) return;
        // Check IP match
        if (message?.type === WS_SERVER_DATA_TYPES.EXTRACTION_PROGRESS && message.ip === currentServerIpRef.current) {
            if (extractionStatus === 'extracting') { // Only update if currently extracting
                setExtractionProgress(message);
            }
        } else {
             if (message?.ip && message.ip !== currentServerIpRef.current) console.warn("Ignored Extraction Progress for IP:", message.ip);
        }
    }, [extractionStatus, currentServerIpRef]); // Added currentServerIpRef

    const handleExtractionErrorUpdate = useCallback((errorMsg, wsError) => {
        // Вызываем общий обработчик ошибок, он должен сам вызвать openModal при необходимости
        const userFriendlyErrorMessage = handleWebSocketError(
            errorMsg, // Это объект {"type":"ERROR","error":"Insufficient plan. Required: Vip"}
            wsError,
            openModal,
            addNotification,
            {
              defaultTitle: 'Server Data Extraction Error', // Исправлено
              moduleName: 'Server Data Extraction'      // Исправлено
            }
        );

        // Логика ниже нужна для обновления состояния UI этого конкретного компонента
        console.error('%cWS SERVER DATA EXTRACTION ERROR Received:', 'color:red;', errorMsg, "Formatted:", userFriendlyErrorMessage);

        // wsError - это ошибка самого WebSocket соединения/протокола, а не бизнес-логики.
        // handleWebSocketError уже должен был учесть wsError.
        // Если wsError существует, userFriendlyErrorMessage уже будет содержать информацию о нем.

        setExtractionError(userFriendlyErrorMessage); // Используем сообщение, обработанное утилитой
        setExtractionStatus('error');
        setIsExtractingLoading(false);
        setExtractionProgress(null); // Сбросить прогресс при ошибке

        // Опционально: если errorMsg содержит isFatal, можно добавить дополнительную логику
        // const isFatal = errorMsg?.isFatal !== undefined ? errorMsg.isFatal : true;
        // if (isFatal) {
        //     // ...
        // }

    }, [openModal, addNotification]); // Добавлены зависимости

    // --- NEW: History Handler ---
    const handleHistoryMessage = useCallback((message, wsError) => {
        setIsHistoryLoading(false); // Stop loading
        if (wsError || message?.type === WS_SERVER_DATA_TYPES.HISTORY_ERROR) {
            const errorText = handleWebSocketError( // Также используем общий обработчик для единообразия
                message, // message может содержать { error: "..." }
                wsError,
                openModal, // Передаем openModal, если вдруг история тоже может выдать plan error
                addNotification,
                {
                    defaultTitle: 'History Error',
                    moduleName: 'Server Data Extraction History'
                }
            );
            console.error("Data Extraction History WS Error:", errorText, "Original message:", message);
            setHistoryError(errorText);
            setExtractionHistory([]);
        } else if (message?.type === WS_SERVER_DATA_TYPES.HISTORY_LIST && message.data?.history) {
             console.log("Received Data Extraction History:", message.data.history);
             const formattedHistory = message.data.history.map(item => ({
                 ...item,
                 date: new Date(item.date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
             }));
             setExtractionHistory(formattedHistory);
             setHistoryError(null);
        } else {
            console.warn("Received unknown/invalid extraction history message:", message);
            // Опционально: setHistoryError("Received unexpected history data format.");
            // setExtractionHistory([]); // Решите, нужно ли очищать историю в этом случае
        }
    }, [openModal, addNotification]); // Добавлены зависимости

    // --- Component Actions ---

    // handleExtractData remains largely the same, uses DataExtraction endpoint
    const handleExtractData = useCallback(() => {
        const currentIp = currentServerIpRef.current;
        if (!isConnectedToServer) {
            setExtractionError("Please connect to a server first."); return;
        }
        if (isExtractingLoading) {
             setExtractionError("Extraction is already in progress."); return;
        }

        console.log(`Requesting data extraction for ${serverInfo.hostname} with options:`, extractionOptions);
        setIsExtractingLoading(true);
        setExtractionStatus('extracting');
        setExtractionError(null);
        setExtractedData(null);
        setExtractionProgress(null);
        setGeneralWsError(null);

        const backendOptions = { ...extractionOptions };
        // Send message to the specific DataExtraction endpoint
        console.log("Extract start: ", getDataAppEndpoint(WS_SERVER_DATA_TOPICS.EXTRACT));
        sendMessage(getDataAppEndpoint(WS_SERVER_DATA_TOPICS.EXTRACT), {
            ip: serverInfo.hostname,
            options: backendOptions
        });
    }, [isConnectedToServer, isExtractingLoading, extractionOptions, sendMessage, serverInfo]); // Added serverInfo for ip in payload

    // --- Refs for stable handlers ---
    const latestHandlersRef = useRef({});

    // --- WebSocket Message Handlers (memoized) ---
    // These are the useCallback definitions from earlier in the file,
    // simply aliased here for clarity before being put into the ref.
    // Ensure their original dependencies are correct.
    const memoizedHandleExtractionStatusUpdate = handleExtractionStatusUpdate;
    const memoizedHandleExtractionProgressUpdate = handleExtractionProgressUpdate;
    const memoizedHandleExtractionErrorUpdate = handleExtractionErrorUpdate;
    const memoizedHandleHistoryMessage = handleHistoryMessage;

    useEffect(() => {
        latestHandlersRef.current = {
            handleExtractionStatusUpdate: memoizedHandleExtractionStatusUpdate,
            handleExtractionProgressUpdate: memoizedHandleExtractionProgressUpdate,
            handleExtractionErrorUpdate: memoizedHandleExtractionErrorUpdate,
            handleHistoryMessage: memoizedHandleHistoryMessage,
        };
    }, [
        memoizedHandleExtractionStatusUpdate,
        memoizedHandleExtractionProgressUpdate,
        memoizedHandleExtractionErrorUpdate,
        memoizedHandleHistoryMessage,
    ]);


    // --- WebSocket Subscriptions and State Recovery ---
    useEffect(() => {
        if (!isWebSocketConnected || !isAuthenticated || isAuthLoading) {
            console.log("ServerDataExtractionPage: WS not ready or auth loading, skipping subscriptions.");
            Object.values(subscriptions.current).forEach(unsub => unsub?.());
            subscriptions.current = { extractionStatus: null, extractionProgress: null, extractionError: null, history: null };
            historyRequestedRef.current = false; // Reset history fetch attempt
            // Potentially clear other states like extractionStatus, error, etc.
            return;
        }

        setGeneralWsError(null); // Clear error on successful WS connection

        console.log("Subscribing to ServerDataExtraction topics with stable handlers...");
        // Ensure any previous subscriptions from this component are cleared before re-subscribing
        // This is important if `subscribe` function identity itself could change, or if this effect runs multiple times.
        Object.values(subscriptions.current).forEach(unsub => unsub?.());


        subscriptions.current.extractionStatus = subscribe(
            getDataUserQueueTopic(WS_SERVER_DATA_TOPICS.STATUS),
            (message, wsError) => latestHandlersRef.current.handleExtractionStatusUpdate?.(message, wsError)
        );
        subscriptions.current.extractionProgress = subscribe(
            getDataUserQueueTopic(WS_SERVER_DATA_TOPICS.PROGRESS),
            (message, wsError) => latestHandlersRef.current.handleExtractionProgressUpdate?.(message, wsError)
        );
        subscriptions.current.extractionError = subscribe(
            getDataUserQueueTopic(WS_SERVER_DATA_TOPICS.ERROR),
            (errorMsg, wsError) => latestHandlersRef.current.handleExtractionErrorUpdate?.(errorMsg, wsError)
        );
        subscriptions.current.history = subscribe(
            getDataUserQueueTopic(WS_SERVER_DATA_TOPICS.USER_HISTORY),
            (message, wsError) => latestHandlersRef.current.handleHistoryMessage?.(message, wsError)
        );

        // Request state *after* subscribing
        const stateRequestTimerId = setTimeout(() => {
            if (isWebSocketConnected && isAuthenticated) { // Double check before sending
                console.log("Requesting current Server Data Extraction state from backend...");
                sendMessage(getDataAppEndpoint(WS_SERVER_DATA_TOPICS.GET_STATE), {});
            }
        }, 500);

        // Request history only once per component lifecycle (or after re-auth/reconnect)
        if (!historyRequestedRef.current) {
            const historyRequestTimerId = setTimeout(() => {
                if (isWebSocketConnected && isAuthenticated) { // Double check
                    console.log("Requesting Data Extraction History...");
                    setIsHistoryLoading(true);
                    setHistoryError(null);
                    sendMessage(getDataAppEndpoint(WS_SERVER_DATA_TOPICS.APP_GET_HISTORY), {});
                    historyRequestedRef.current = true; // Mark as requested
                }
            }, 600);
             // Cleanup for history timer
            return () => clearTimeout(historyRequestTimerId);
        }


        return () => {
            console.log("Unsubscribing from ServerDataExtraction topics (effect cleanup)...");
            Object.values(subscriptions.current).forEach(unsub => unsub?.());
            subscriptions.current = { extractionStatus: null, extractionProgress: null, extractionError: null, history: null };
            clearTimeout(stateRequestTimerId);
            // Note: historyRequestTimerId cleanup is handled if the history block runs
        };
    }, [isWebSocketConnected, isAuthenticated, isAuthLoading, subscribe, sendMessage]);
    // Removed specific handlers from dependencies as they are now called via ref.
    // `serverInfo` and `connectionError` are from `useServerConnection` and don't directly control these subs.


    // --- History (Mock Load - Keep as is) ---
     useEffect(() => {
        const loadMockHistory = () => {
             const mockHistory = [
                 { server: "play.hypixel.net", ip: "172.65.230.10", date: "2023-06-15T14:32:17Z", status: "complete", extractedData: ["info", "players", "plugins", "motd", "serverIcon"] },
                 { server: "mc.cubecraft.net", ip: "193.70.80.103", date: "2023-06-14T09:12:00Z", status: "complete", extractedData: ["info", "players", "motd", "serverIcon"] },
                 { server: "play.mineville.org", ip: "104.219.248.86", date: "2023-06-12T17:45:00Z", status: "partial", extractedData: ["info", "plugins"] }
             ];
             setExtractionHistory(mockHistory);
        };
        loadMockHistory();
     }, []);


    // --- Tips (Static - Keep as is) ---
    const extractionTips = [
        { icon: 'fa-user-secret', title: 'Player Sampling', description: 'Many servers only send a sample of online players. Deep Scan might reveal more.' },
        { icon: 'fa-puzzle-piece', title: 'Plugin Detection', description: 'Plugin lists are often hidden. Success depends on server configuration.' },
        { icon: 'fa-shield-alt', title: 'Timeout Settings', description: 'Increase timeout for slow servers, but be cautious of security flags.' },
    ];

    // --- Derived State for UI ---
    // Main error to display (prioritize connection errors)
    const displayError = connectionError || generalWsError;
    // Options are enabled only when connected and not extracting
    const optionsDisabled = !isConnectedToServer || isConnecting || isExtractingLoading;
    // Results section is disabled if connecting OR no server info is available yet
    const resultsDisabled = isConnecting || !serverInfo;

    // --- Render ---
    return (
        <PageTransition>
            <div className="p-6 lg:p-8">
                <ServerDataExtractionHeader />
                <ServerInfoCard />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    {/* Extraction Options */}
                    <div className={`lg:col-span-1 transition-opacity duration-300 ${optionsDisabled ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
                       <AnimatePresence>
                           {/* Show options only when connected and not connecting */}
                           {(
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                                    <ExtractionOptions
                                        options={extractionOptions}
                                        onChange={setExtractionOptions}
                                        onExtract={handleExtractData}
                                        isLoading={isExtractingLoading} // Loading state specific to extraction
                                        disabled={optionsDisabled} // Use calculated state
                                    />
                                </motion.div>
                           )}
                        </AnimatePresence>
                        {/* Placeholder when not connected */}
                         {/*!isConnectedToServer && !isConnecting && (
                             <div className="bg-dark-600 rounded-2xl p-6 h-full flex items-center justify-center text-gray-500 border border-dark-400 min-h-[400px]">
                                 Connect to a server to configure extraction options.
                             </div>
                         )*/}
                         {/* Placeholder when connecting */}
                          {isConnecting && (
                             <div className="bg-dark-600 rounded-2xl p-6 h-full animate-pulse min-h-[400px]">
                                 <div className="h-8 bg-dark-500 rounded w-3/4 mb-6"></div>
                                 <div className="space-y-4">{[...Array(6)].map((_, i) => <div key={i} className="h-10 bg-dark-500 rounded"></div>)}<div className="h-10 bg-dark-500 rounded mt-6"></div><div className="h-12 bg-dark-400 rounded mt-6"></div></div>
                             </div>
                         )}
                    </div>

                    {/* Extraction Results */}
                    <div className="lg:col-span-2">
                         <ExtractionResults
                            serverData={extractedData || serverInfo} // Show extracted data, fallback to initial server info
                            extractionStatus={extractionStatus} // Pass extraction status
                            isLoading={isExtractingLoading || isConnecting} // Show loading for either process
                            isDisabled={resultsDisabled} // Disable if connecting or no server info
                         />
                         {/* Show extraction-specific error */}
                         {extractionError && extractionStatus === 'error' && (
                             <motion.p initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="text-accent-red text-xs mt-2 text-center lg:text-left">
                                <i className="fas fa-exclamation-circle mr-1"></i> Extraction failed: {extractionError}
                            </motion.p>
                         )}
                         {/* Show extraction-specific progress */}
                         {extractionProgress && extractionStatus === 'extracting' && (
                              <motion.p key={extractionProgress.step + extractionProgress.message} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-accent-blue text-xs mt-2 text-center lg:text-left">
                                <i className="fas fa-spinner fa-spin mr-1"></i> {extractionProgress.step}: {extractionProgress.message}
                             </motion.p>
                         )}
                    </div>
                </div>
                {/* History */}
                <div className="mb-8">
                <ExtractionHistory
                        history={extractionHistory}
                        isLoading={isHistoryLoading}
                        error={historyError}
                    />
                </div>
                {/* Tips */}
                <Tips
                    title="Data Extraction Tips"
                    icon="fa-lightbulb"
                    tips={extractionTips}
                    accentColor="blue"
                />
            </div>
        </PageTransition>
    );
};

export default ServerDataExtractionPage;